/**
 * Centralized project-display-name resolver.
 *
 * Multiple parts of the codebase render project names from objects that come
 * from different API responses — `programs`, `quotes`, `change_orders`, etc.
 * The field name varies (`program_name`, `project_name`, `displayName`, …) and
 * historically components have implemented their own fallback chain, which
 * leads to "Untitled" labels showing up whenever a new shape is introduced.
 *
 * Use this helper EVERYWHERE a project name is rendered. Never hand-roll
 * `p.name || "Untitled"` again.
 */
export function getProjectDisplayName(project) {
  if (!project || typeof project !== "object") return "Untitled Project";

  const trim = (s) => (typeof s === "string" ? s.trim() : "");

  // Direct name fields, in priority order. Supports BOTH camelCase and
  // snake_case so callers don't need to remap their payloads.
  const direct =
    trim(project.projectName) ||
    trim(project.project_name) ||
    trim(project.programName) ||
    trim(project.program_name) ||
    trim(project.displayName) ||
    trim(project.display_name) ||
    trim(project.name) ||
    trim(project.title) ||
    trim(project.clientProjectName) ||
    trim(project.client_project_name) ||
    trim(project.address);

  if (direct) return direct;

  // Derived fallback: build "<Client> Project" when only a client name exists.
  const clientName = trim(project.clientName) || trim(project.client_name);
  if (clientName) return `${clientName} Project`;

  return "Untitled Project";
}

/**
 * Resolve the assigned PM name for a project across the many shapes our API
 * returns. The TRP HQ programs API stores the Visualization PM in
 * `primary_pm_name` (with `primary_pm_id`) and the Program Manager in
 * `program_manager_name`. Older shapes used `pm_name` / `pmId`. This helper
 * tries them all and finally falls back to looking the ID up in a `users`
 * array if a name wasn't joined onto the row.
 */
export function getProjectPMDisplayName(project, users = []) {
  if (!project || typeof project !== "object") return "Unassigned";

  const trim = (s) => (typeof s === "string" ? s.trim() : "");

  const directName =
    trim(project.primary_pm_name) ||
    trim(project.primaryPMName) ||
    trim(project.pm_name) ||
    trim(project.pmName) ||
    trim(project.projectManagerName) ||
    trim(project.project_manager_name) ||
    trim(project.assignedPMName) ||
    trim(project.assigned_pm_name) ||
    trim(project.assignedProjectManagerName) ||
    trim(project.assigned_project_manager_name) ||
    trim(project.program_manager_name) ||
    trim(project.programManagerName) ||
    trim(project.projectManager?.name) ||
    trim(project.assignedPM?.name) ||
    trim(project.pm?.name);

  if (directName) return directName;

  const pmId =
    project.primary_pm_id ||
    project.primaryPMId ||
    project.pm_id ||
    project.pmId ||
    project.project_manager_id ||
    project.projectManagerId ||
    project.assigned_pm_id ||
    project.assignedPMId ||
    project.assigned_project_manager_id ||
    project.assignedProjectManagerId ||
    project.visualization_pm_id ||
    project.visualizationPMId ||
    project.program_manager_id ||
    project.programManagerId ||
    project.owner_id ||
    project.ownerId;

  if (pmId && Array.isArray(users) && users.length) {
    const matched = users.find((u) => u && (u.id === pmId || u._id === pmId));
    if (matched) {
      return trim(matched.full_name) || trim(matched.fullName) || trim(matched.name) || trim(matched.email) || "Unassigned";
    }
  }

  return "Unassigned";
}

/** Resolve the PM id behind a project (used for auto-populating routing). */
export function getProjectPMId(project) {
  if (!project || typeof project !== "object") return null;
  return (
    project.primary_pm_id ||
    project.primaryPMId ||
    project.pm_id ||
    project.pmId ||
    project.project_manager_id ||
    project.projectManagerId ||
    project.assigned_pm_id ||
    project.assignedPMId ||
    project.program_manager_id ||
    project.programManagerId ||
    null
  );
}

export default getProjectDisplayName;
